import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Video, Clock, CheckCircle2, AlertCircle, Play, MessageSquare } from 'lucide-react';
import VideoRecorder from './VideoRecorder';
import ClarificationDialog from './ClarificationDialog';

/**
 * VideoSequenceFlow Component
 * 
 * Implements the sequential video confirmation workflow
 */
export default function VideoSequenceFlow({ agreement, onRefresh }) {
  const { user, token } = useAuth();
  const { t, getTemplateLabel } = useLanguage();
  const [showRecorder, setShowRecorder] = useState(false);
  const [showClarification, setShowClarification] = useState(false);
  const [secondaryVideo, setSecondaryVideo] = useState(null);
  const [principalVideo, setPrincipalVideo] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [canRecord, setCanRecord] = useState(false);

  useEffect(() => {
    if (!agreement || !user) return;

    // Determine user's role
    const role = agreement.created_by_user_id === user.id ? 'LENDER' : 'BORROWER';
    setUserRole(role);

    // Find video confirmations - get the latest for each role
    const videosList = [...(agreement.video_confirmations || [])].sort((a, b) => {
      const dateA = new Date(a.created_at || a.timestamp_utc || 0).getTime();
      const dateB = new Date(b.created_at || b.timestamp_utc || 0).getTime();
      return dateB - dateA; // Newest first
    });
    
    // Find latest video for each party
    const secondaryVid = videosList.find(v => v.user_id !== agreement.created_by_user_id);
    const principalVid = videosList.find(v => v.user_id === agreement.created_by_user_id);

    setSecondaryVideo(secondaryVid);
    setPrincipalVideo(principalVid);

    // Determine if user can record
    if (role === 'BORROWER') {
      // Secondary party can record if they haven't already OR if clarification is requested, and agreement is not locked
      setCanRecord((!secondaryVid || agreement.status === 'CLARIFICATION_REQUESTED') && agreement.status !== 'LOCKED');
    } else if (role === 'LENDER') {
      // Principal party can record only if:
      // 1. Secondary party has confirmed
      // 2. No clarification is pending
      // 3. Principal hasn't recorded yet
      // 4. Agreement is not locked
      setCanRecord(
        secondaryVid &&
        agreement.status !== 'CLARIFICATION_REQUESTED' &&
        !principalVid &&
        agreement.status !== 'LOCKED'
      );
    }
  }, [agreement, user]);

  const handleRecordingSuccess = () => {
    setShowRecorder(false);
    onRefresh?.();
  };

  // Loading state
  if (!agreement || !user || userRole === null) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const templateType = agreement.agreement_template || 'LOAN';
  const principalLabel = getTemplateLabel(templateType, 'principal');
  const secondaryLabel = getTemplateLabel(templateType, 'secondary');

  // Both parties have confirmed
  if (secondaryVideo && principalVideo) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            {t('loan.status.both_confirmed')}
          </CardTitle>
          <CardDescription>
            {t('video.list.complete_record')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border rounded-sm p-4 space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="font-medium text-sm">{secondaryLabel}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('video.list.recorded_on')} {new Date(secondaryVideo.created_at || secondaryVideo.timestamp_utc).toLocaleDateString()}
              </p>
              <div className="pt-1">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${secondaryVideo.storage_path}?auth=${token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-green-700 hover:text-green-800 font-medium"
                >
                  <Play className="w-3 h-3" />
                  {t('video.record.view_secondary_video')}
                </a>
              </div>
            </div>
            <div className="border rounded-sm p-4 space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="font-medium text-sm">{principalLabel}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('video.list.recorded_on')} {new Date(principalVideo.created_at || principalVideo.timestamp_utc).toLocaleDateString()}
              </p>
              <div className="pt-1">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${principalVideo.storage_path}?auth=${token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-green-700 hover:text-green-800 font-medium"
                >
                  <Play className="w-3 h-3" />
                  {t('video.record.view_principal_video') || 'View Principal Video'}
                </a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const lender = agreement.participants?.find(p => p.role === 'LENDER');
  const borrower = agreement.participants?.find(p => p.role === 'BORROWER');
  const lenderName = lender?.user_name || 'the principal party';
  const borrowerName = borrower?.user_name || 'the secondary party';

  // Secondary party view - can record
  if (userRole === 'BORROWER' && !secondaryVideo) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-primary" />
            {t('video.record.record_your_confirmation')}
          </CardTitle>
          <CardDescription>
            {t('video.record.waiting_for_secondary')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-primary/5 border border-primary/10 p-4 rounded-sm space-y-2">
            <p className="text-sm font-medium">{t('video.record.title')}</p>
            <p className="text-xs text-muted-foreground">
              {t('video.record.read_statement')}
            </p>
          </div>
          <Button
            onClick={() => setShowRecorder(true)}
            disabled={!canRecord}
            className="w-full"
          >
            <Video className="w-4 h-4 mr-2" />
            {t('video.record.start_recording')}
          </Button>
        </CardContent>

        <VideoRecorder
          loan={agreement}
          lenderName={lenderName}
          borrowerName={borrowerName}
          open={showRecorder}
          onOpenChange={setShowRecorder}
          onSuccess={handleRecordingSuccess}
        />
      </Card>
    );
  }

  // Secondary party has recorded, waiting for principal
  if (userRole === 'BORROWER' && secondaryVideo && !principalVideo) {
    if (agreement.status === 'CLARIFICATION_REQUESTED') {
      const reason = agreement.pending_clarification?.reason || (t('loan.clarification.reason_placeholder') || 'Please provide more details.');
      return (
        <Card className="border-orange-200">
          <CardHeader className="bg-orange-50/50">
            <CardTitle className="flex items-center gap-2 text-orange-900">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              {t('loan.clarification.request_title')}
            </CardTitle>
            <CardDescription className="text-orange-800/80">
              {t('loan.clarification.reconfirm_message')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            <div className="bg-white border border-orange-200 p-4 rounded-sm space-y-2 shadow-sm">
              <p className="text-sm font-semibold text-orange-900 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                {t('loan.clarification.clarification_reason')}:
              </p>
              <p className="text-sm text-orange-800 italic">"{reason}"</p>
            </div>
            <Button
              onClick={() => setShowRecorder(true)}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Video className="w-4 h-4 mr-2" />
              {t('loan.clarification.reconfirm_button')}
            </Button>
          </CardContent>

          <VideoRecorder
            loan={agreement}
            lenderName={lenderName}
            borrowerName={borrowerName}
            open={showRecorder}
            onOpenChange={setShowRecorder}
            onSuccess={handleRecordingSuccess}
            isReconfirmation={true}
          />
        </Card>
      );
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            {t('video.record.success')}
          </CardTitle>
          <CardDescription>
            {t('video.record.waiting_for_secondary')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-green-50 border border-green-200 p-4 rounded-sm space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-900">{t('loan.confirmation.success')}</span>
            </div>
            <p className="text-xs text-green-700">
              {t('video.list.recorded_on')} {new Date(secondaryVideo.created_at).toLocaleString()}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Principal party view - waiting for secondary
  if (userRole === 'LENDER' && !secondaryVideo) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" />
            {t('loan.status.pending_confirmation')}
          </CardTitle>
          <CardDescription>
            {t('video.record.waiting_for_secondary')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm space-y-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-900">{t('video.record.title')}</span>
            </div>
            <p className="text-xs text-amber-700">
              {t('video.record.waiting_for_secondary')}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Principal party view - can view secondary video and record
  if (userRole === 'LENDER' && secondaryVideo && !principalVideo) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-primary" />
            {t('video.record.record_your_confirmation')}
          </CardTitle>
          <CardDescription>
            {t('video.record.view_secondary_video')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-green-50 border border-green-200 p-4 rounded-sm space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-900">{secondaryLabel} {t('loan.confirmation.success')}</span>
            </div>
            <div className="pt-2">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${secondaryVideo.storage_path}?auth=${token}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs font-medium text-green-700 hover:text-green-800"
              >
                <Play className="w-3 h-3" />
                {t('video.record.view_secondary_video')}
              </a>
            </div>
          </div>

          {agreement.status === 'CLARIFICATION_REQUESTED' ? (
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-900">{t('loan.clarification.pending_message')}</span>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowRecorder(true)}
                  disabled={!canRecord}
                  className="w-full flex-1"
                >
                  <Video className="w-4 h-4 mr-2" />
                  {t('video.record.record_your_confirmation')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowClarification(true)}
                  disabled={!canRecord}
                  className="w-full flex-1 text-amber-700 border-amber-200 hover:bg-amber-50"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  {t('loan.clarification.request_button')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>

        <VideoRecorder
          loan={agreement}
          lenderName={lenderName}
          borrowerName={borrowerName}
          open={showRecorder}
          onOpenChange={setShowRecorder}
          onSuccess={handleRecordingSuccess}
        />

        <ClarificationDialog
          open={showClarification}
          onOpenChange={setShowClarification}
          agreement={agreement}
          onSuccess={onRefresh}
        />
      </Card>
    );
  }

  // Fallback
  return null;
}
